import React, { useMemo } from "react";
import { RigidBody } from "@react-three/rapier";
import { getFenceTexture } from "./textures";

interface CompoundFenceProps {
  radius?: number;
  segments?: number;
  gateHalfAngle?: number; // radians — gap centered on local +Z (the approach side)
  height?: number;
}

/** A ring of fence segments around the local origin, with a gate gap on the +Z (approach) side. */
const CompoundFence: React.FC<CompoundFenceProps> = ({
  radius = 3.4,
  segments = 12,
  gateHalfAngle = 0.55,
  height = 1.6,
}) => {
  const fenceTex = useMemo(() => {
    const tex = getFenceTexture().clone();
    tex.needsUpdate = true;
    tex.repeat.set(2, 1);
    return tex;
  }, []);

  const segmentAngle = (Math.PI * 2) / segments;
  const segmentWidth = 2 * radius * Math.tan(segmentAngle / 2) * 1.05;

  const posts = useMemo(() => {
    const arr: { angle: number; x: number; z: number }[] = [];
    for (let i = 0; i < segments; i++) {
      const angle = i * segmentAngle;
      const normalized = Math.atan2(Math.sin(angle), Math.cos(angle));
      if (Math.abs(normalized) < gateHalfAngle) continue; // leave the gate open
      arr.push({ angle, x: Math.sin(angle) * radius, z: Math.cos(angle) * radius });
    }
    return arr;
  }, [segments, segmentAngle, gateHalfAngle, radius]);

  return (
    <group>
      {posts.map((p, i) => (
        <RigidBody key={i} type="fixed" position={[p.x, height / 2, p.z]} rotation={[0, p.angle, 0]} colliders="cuboid">
          <mesh castShadow receiveShadow>
            <boxGeometry args={[segmentWidth, height, 0.14]} />
            <meshStandardMaterial map={fenceTex} roughness={1} />
          </mesh>
        </RigidBody>
      ))}
    </group>
  );
};

export default CompoundFence;
